import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CurrencyInr, Receipt, CreditCard,
  Plus, Trash, PencilSimple, X, CaretDown,
  Warning, CheckCircle,
  FilePdf, FileText, DownloadSimple, LinkSimple, ArrowsLeftRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BILL_TYPES = ['RA Bill', 'Final Bill', 'VO Bill', 'Mixed'];
const BILL_STATUSES = ['Draft', 'Submitted', 'Approved', 'Partially Paid', 'Paid'];
const PAYMENT_MODES = ['Bank Transfer', 'Cheque', 'Cash', 'UPI', 'DD', 'Other'];

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${className}`}>{children}</span>
);
const BILL_STATUS_COLORS = {
  Draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  Submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  Approved: 'bg-green-50 text-green-700 border-green-200',
  'Partially Paid': 'bg-amber-50 text-amber-700 border-amber-200',
  Paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const INVOICE_STATUS_COLORS = {
  'Not Invoiced': 'bg-zinc-100 text-zinc-600 border-zinc-200',
  'Partially Invoiced': 'bg-amber-50 text-amber-700 border-amber-200',
  'Fully Invoiced': 'bg-green-50 text-green-700 border-green-200',
};
const fmt = (v) => `Rs.${(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export const ProjectFinancials = ({ projectId }) => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [taxInvoices, setTaxInvoices] = useState([]);
  const [billingContext, setBillingContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');

  // Bill form
  const [billModal, setBillModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [billForm, setBillForm] = useState({ bill_type: 'RA Bill', description: '', bill_amount: '', notes: '', bill_date: '', due_date: '', linked_vo_ids: [] });
  const [billLineItems, setBillLineItems] = useState([]);

  // Payment form
  const [payModal, setPayModal] = useState(false);
  const [payForm, setPayForm] = useState({ bill_id: '', amount: '', mode: 'Bank Transfer', reference_number: '', remarks: '', payment_date: '' });

  // Tax Invoice form
  const [tiModal, setTiModal] = useState(false);
  const [editingTi, setEditingTi] = useState(null);
  const [tiForm, setTiForm] = useState({ invoice_number: '', invoice_date: '', invoice_value: '', linked_bill_ids: [], remarks: '', eway_required: false, eway_number: '', eway_date: '', tax_type: 'cgst_sgst', tax_rate: '18' });

  const [statusDropdown, setStatusDropdown] = useState(null);
  const [enforcementModal, setEnforcementModal] = useState(null); // { blocks, onOverride, onCancel }
  const [overrideReason, setOverrideReason] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [summaryRes, billsRes, paymentsRes, tiRes, ctxRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/financial-summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/bills`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/payments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/tax-invoices`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/billing-context`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setSummary(summaryRes.data);
      setBills(billsRes.data);
      setPayments(paymentsRes.data);
      setTaxInvoices(tiRes.data);
      setBillingContext(ctxRes.data);
    } catch { toast.error('Failed to load financial data'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- BILL CRUD ----
  const openBillModal = (bill = null) => {
    if (bill) {
      setEditingBill(bill);
      setBillForm({
        bill_type: bill.bill_type, description: bill.description,
        bill_amount: bill.bill_amount.toString(), notes: bill.notes,
        bill_date: bill.bill_date ? bill.bill_date.split('T')[0] : '',
        due_date: bill.due_date ? bill.due_date.split('T')[0] : '',
        linked_vo_ids: bill.linked_vo_ids || [],
      });
      setBillLineItems(bill.line_items || []);
    } else {
      setEditingBill(null);
      setBillForm({ bill_type: 'RA Bill', description: '', bill_amount: '', notes: '', bill_date: '', due_date: '', linked_vo_ids: [] });
      setBillLineItems([]);
    }
    setBillModal(true);
  };

  const handleSaveBill = async () => {
    if (!billForm.bill_amount || parseFloat(billForm.bill_amount) <= 0) { toast.error('Enter a valid amount'); return; }

    // Enforcement check before creating new bill
    if (!editingBill) {
      try {
        const check = await axios.post(`${BACKEND_URL}/api/enforcement/check-bill-submission`, {
          project_id: projectId,
          bill_id: null,
          linked_vo_ids: billForm.linked_vo_ids,
        }, { withCredentials: true });

        if (!check.data.can_proceed && check.data.blocks?.length > 0) {
          setEnforcementModal({
            blocks: check.data.blocks,
            title: 'Bill Submission Blocked',
            onOverride: async (reason) => {
              for (const block of check.data.blocks) {
                await axios.post(`${BACKEND_URL}/api/enforcement/override`, {
                  rule_type: block.rule,
                  entity_type: 'bill',
                  entity_id: 'new',
                  project_id: projectId,
                  reason,
                  affected_ids: (block.data || []).map?.(v => v.vo_id) || [],
                  financial_impact: block.total_impact || 0,
                }, { withCredentials: true });
              }
              await doSaveBill();
            },
          });
          return;
        }
      } catch { /* proceed if enforcement check fails */ }
    }
    await doSaveBill();
  };

  const doSaveBill = async () => {
    try {
      const payload = {
        project_id: projectId,
        bill_type: billForm.bill_type,
        description: billForm.description,
        bill_amount: parseFloat(billForm.bill_amount),
        notes: billForm.notes,
        linked_vo_ids: billForm.linked_vo_ids,
        line_items: billLineItems.filter(li => li.description && li.amount),
      };
      if (billForm.bill_date) payload.bill_date = new Date(billForm.bill_date).toISOString();
      if (billForm.due_date) payload.due_date = new Date(billForm.due_date).toISOString();

      if (editingBill) {
        await axios.put(`${BACKEND_URL}/api/bills/${editingBill.id}`, {
          description: billForm.description, bill_amount: parseFloat(billForm.bill_amount),
          notes: billForm.notes, linked_vo_ids: billForm.linked_vo_ids,
          line_items: billLineItems.filter(li => li.description && li.amount),
          bill_date: billForm.bill_date ? new Date(billForm.bill_date).toISOString() : undefined,
          due_date: billForm.due_date ? new Date(billForm.due_date).toISOString() : undefined,
        }, { withCredentials: true });
        toast.success('Bill updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/projects/${projectId}/bills`, payload, { withCredentials: true });
        toast.success('Bill created');
      }
      setBillModal(false); setEditingBill(null); setEnforcementModal(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save bill'); }
  };

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Delete this bill?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/bills/${billId}`, { withCredentials: true });
      toast.success('Bill deleted'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete bill'); }
  };

  const handleStatusChange = async (billId, newStatus) => {
    // Enforcement check for bill completion
    if (['Approved', 'Paid'].includes(newStatus)) {
      try {
        const check = await axios.post(`${BACKEND_URL}/api/enforcement/check-bill-completion`, {
          bill_id: billId, new_status: newStatus,
        }, { withCredentials: true });

        if (!check.data.can_proceed && check.data.blocks?.length > 0) {
          setStatusDropdown(null);
          setEnforcementModal({
            blocks: check.data.blocks,
            title: `Bill ${newStatus} Blocked`,
            onOverride: async (reason) => {
              for (const block of check.data.blocks) {
                await axios.post(`${BACKEND_URL}/api/enforcement/override`, {
                  rule_type: block.rule,
                  entity_type: 'bill',
                  entity_id: billId,
                  project_id: projectId,
                  reason,
                  financial_impact: block.data?.bill_amount || 0,
                }, { withCredentials: true });
              }
              await doStatusChange(billId, newStatus);
            },
          });
          return;
        }
      } catch { /* proceed if enforcement check fails */ }
    }
    await doStatusChange(billId, newStatus);
  };

  const doStatusChange = async (billId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/bills/${billId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`); setStatusDropdown(null); setEnforcementModal(null); fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  // ---- PAYMENT CRUD ----
  const handleCreatePayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      const payload = { project_id: projectId, amount: parseFloat(payForm.amount), mode: payForm.mode, reference_number: payForm.reference_number, remarks: payForm.remarks };
      if (payForm.bill_id) payload.bill_id = payForm.bill_id;
      if (payForm.payment_date) payload.payment_date = new Date(payForm.payment_date).toISOString();
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/payments`, payload, { withCredentials: true });
      toast.success('Payment recorded'); setPayModal(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/payments/${paymentId}`, { withCredentials: true });
      toast.success('Payment deleted'); fetchAll();
    } catch { toast.error('Failed to delete payment'); }
  };

  // ---- TAX INVOICE CRUD ----
  const openTiModal = (ti = null) => {
    if (ti) {
      setEditingTi(ti);
      setTiForm({
        invoice_number: ti.invoice_number, invoice_date: ti.invoice_date?.split('T')[0] || '',
        invoice_value: ti.invoice_value.toString(), linked_bill_ids: ti.linked_bill_ids || [],
        remarks: ti.remarks || '', eway_required: ti.eway_required || false,
        eway_number: ti.eway_number || ti.eway_bill_number || '', eway_date: ti.eway_date?.split('T')[0] || '',
        tax_type: ti.tax_type || 'cgst_sgst', tax_rate: (ti.tax_rate || 18).toString(),
        ship_to_gstin: ti.ship_to_gstin || '', eway_bill_validity_date: ti.eway_bill_validity_date?.split('T')[0] || '',
        from_state_code: ti.from_state_code || '', is_interstate: ti.is_interstate || false, is_job_work: ti.is_job_work || false,
        hsn_codes: (ti.hsn_codes || []).join(', '),
      });
    } else {
      setEditingTi(null);
      setTiForm({ invoice_number: '', invoice_date: '', invoice_value: '', linked_bill_ids: [], remarks: '', eway_required: false, eway_number: '', eway_date: '', tax_type: 'cgst_sgst', tax_rate: '18', ship_to_gstin: '', eway_bill_validity_date: '', from_state_code: '', is_interstate: false, is_job_work: false, hsn_codes: '' });
    }
    setTiModal(true);
  };

  const handleSaveTi = async () => {
    if (!tiForm.invoice_number.trim()) { toast.error('Invoice number required'); return; }
    if (!tiForm.invoice_value || parseFloat(tiForm.invoice_value) <= 0) { toast.error('Enter a valid invoice value'); return; }
    try {
      const payload = {
        project_id: projectId,
        invoice_number: tiForm.invoice_number,
        invoice_value: parseFloat(tiForm.invoice_value),
        linked_bill_ids: tiForm.linked_bill_ids,
        remarks: tiForm.remarks,
        eway_required: tiForm.eway_required,
        eway_number: tiForm.eway_number,
        eway_bill_number: tiForm.eway_number,
        ship_to_gstin: tiForm.ship_to_gstin || '',
        from_state_code: tiForm.from_state_code || '',
        is_interstate: !!tiForm.is_interstate,
        is_job_work: !!tiForm.is_job_work,
        hsn_codes: (tiForm.hsn_codes || '').split(',').map(s => s.trim()).filter(Boolean),
        tax_type: tiForm.tax_type,
        tax_rate: parseFloat(tiForm.tax_rate) || 18,
      };
      if (tiForm.invoice_date) payload.invoice_date = new Date(tiForm.invoice_date).toISOString();
      if (tiForm.eway_date) payload.eway_date = new Date(tiForm.eway_date).toISOString();
      if (tiForm.eway_bill_validity_date) payload.eway_bill_validity_date = new Date(tiForm.eway_bill_validity_date).toISOString();

      if (editingTi) {
        await axios.put(`${BACKEND_URL}/api/tax-invoices/${editingTi.id}`, payload, { withCredentials: true });
        toast.success('Tax Invoice updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/projects/${projectId}/tax-invoices`, payload, { withCredentials: true });
        toast.success('Tax Invoice registered');
      }
      setTiModal(false); setEditingTi(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save tax invoice'); }
  };

  const handleDeleteTi = async (tiId) => {
    if (!window.confirm('Delete this tax invoice?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tax-invoices/${tiId}`, { withCredentials: true });
      toast.success('Tax Invoice deleted'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (loading) return <div className="py-12 text-center text-zinc-400 animate-pulse">Loading financials...</div>;

  const s = summary || {};
  const ageing = s.ageing || {};
  const views = [
    { key: 'overview', label: 'Overview' },
    { key: 'bills', label: `Bills (${bills.length})` },
    { key: 'tax-invoices', label: `Tax Invoices (${taxInvoices.length})` },
    { key: 'payments', label: `Payments (${payments.length})` },
    { key: 'reconciliation', label: 'Reconciliation' },
    { key: 'ageing', label: 'Ageing' },
  ];

  return (
    <div className="space-y-4" data-testid="project-financials">
      {/* VO Billing Warning */}
      {billingContext && billingContext.has_unbilled_vos && (
        <VOBillingWarning context={billingContext} />
      )}

      {/* Sub-nav */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-0.5 flex-wrap">
          {views.map(v => (
            <button key={v.key} onClick={() => setActiveView(v.key)} data-testid={`fin-tab-${v.key}`}
              className={`px-3 py-1.5 text-xs font-medium ${activeView === v.key ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100 border border-zinc-200'}`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openBillModal()} variant="outline" className="rounded-none border-zinc-300 text-xs" data-testid="create-bill-btn">
            <Receipt size={14} className="mr-1.5" /> New Bill
          </Button>
          <Button onClick={() => openTiModal()} variant="outline" className="rounded-none border-blue-300 text-blue-700 text-xs" data-testid="create-ti-btn">
            <FileText size={14} className="mr-1.5" /> Register Tax Invoice
          </Button>
          <Button onClick={() => { setPayForm({ bill_id: '', amount: '', mode: 'Bank Transfer', reference_number: '', remarks: '', payment_date: '' }); setPayModal(true); }}
            className="bg-zinc-900 text-white rounded-none text-xs" data-testid="record-payment-btn">
            <CreditCard size={14} className="mr-1.5" /> Record Payment
          </Button>
        </div>
      </div>

      {/* OVERVIEW */}
      {activeView === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Contract Value', value: fmt(s.contract_value), sub: s.vo_count > 0 ? `+${s.vo_count} VOs: ${fmt(s.vo_value)}` : null, color: 'text-zinc-900' },
              { label: 'Total Project Value', value: fmt(s.total_project_value), color: 'text-blue-700' },
              { label: 'Total Billed', value: fmt(s.total_billed), sub: s.total_draft_bills > 0 ? `${fmt(s.total_draft_bills)} in draft` : null, color: 'text-violet-700' },
              { label: 'Total Invoiced', value: fmt(s.total_invoiced), sub: s.billed_vs_invoiced_gap > 0 ? `Gap: ${fmt(s.billed_vs_invoiced_gap)}` : null, color: 'text-indigo-700' },
              { label: 'Total Received', value: fmt(s.total_received), sub: `${s.collection_percentage}% collected`, color: 'text-green-700' },
            ].map((card, i) => (
              <div key={i} className="bg-white border border-zinc-200 p-3.5" data-testid={`fin-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">{card.label}</div>
                <div className={`text-xl font-bold mt-1 ${card.color}`}>{card.value}</div>
                {card.sub && <div className="text-[11px] text-zinc-400 mt-0.5">{card.sub}</div>}
              </div>
            ))}
          </div>

          {/* Gap Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white border border-zinc-200 p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Outstanding</span>
                <span className={`text-lg font-bold ${s.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="outstanding-amount">{fmt(s.outstanding)}</span>
              </div>
              {s.total_billed > 0 && (
                <div className="w-full h-2.5 bg-zinc-100 overflow-hidden"><div className="h-full bg-green-500 transition-all" style={{ width: `${Math.min(100, s.collection_percentage)}%` }}></div></div>
              )}
              <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
                <span>Received: {fmt(s.total_received)}</span><span>Billed: {fmt(s.total_billed)}</span>
              </div>
            </div>
            <div className={`bg-white border p-4 ${s.billed_vs_invoiced_gap > 0 ? 'border-amber-300' : 'border-zinc-200'}`}>
              <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Billed vs Invoiced</span>
              <div className={`text-lg font-bold mt-1 ${s.billed_vs_invoiced_gap > 0 ? 'text-amber-700' : 'text-green-600'}`}>
                {s.billed_vs_invoiced_gap > 0 ? `Gap: ${fmt(s.billed_vs_invoiced_gap)}` : 'Aligned'}
              </div>
              <div className="text-[10px] text-zinc-400 mt-0.5">Billed: {fmt(s.total_billed)} | Invoiced: {fmt(s.total_invoiced)}</div>
            </div>
            <div className={`bg-white border p-4 ${s.invoiced_vs_received_gap > 0 ? 'border-orange-300' : 'border-zinc-200'}`}>
              <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Invoiced vs Received</span>
              <div className={`text-lg font-bold mt-1 ${s.invoiced_vs_received_gap > 0 ? 'text-orange-700' : 'text-green-600'}`}>
                {s.invoiced_vs_received_gap > 0 ? `Gap: ${fmt(s.invoiced_vs_received_gap)}` : 'Collected'}
              </div>
              <div className="text-[10px] text-zinc-400 mt-0.5">Invoiced: {fmt(s.total_invoiced)} | Received: {fmt(s.total_received)}</div>
            </div>
          </div>

          {/* VO Billing Tracker */}
          {billingContext && billingContext.vo_billing_tracker?.length > 0 && (
            <VOBillingTracker tracker={billingContext.vo_billing_tracker} />
          )}
        </div>
      )}

      {/* BILLS VIEW */}
      {activeView === 'bills' && (
        <BillsView bills={bills} statusDropdown={statusDropdown} setStatusDropdown={setStatusDropdown}
          handleStatusChange={handleStatusChange} handleDeleteBill={handleDeleteBill} openBillModal={openBillModal}
          taxInvoices={taxInvoices} formatDate={formatDate} />
      )}

      {/* TAX INVOICES VIEW */}
      {activeView === 'tax-invoices' && (
        <TaxInvoicesView taxInvoices={taxInvoices} bills={bills} handleDeleteTi={handleDeleteTi}
          openTiModal={openTiModal} formatDate={formatDate} user={user} />
      )}

      {/* PAYMENTS VIEW */}
      {activeView === 'payments' && (
        <PaymentsView payments={payments} handleDeletePayment={handleDeletePayment} formatDate={formatDate} />
      )}

      {/* RECONCILIATION VIEW */}
      {activeView === 'reconciliation' && (
        <ReconciliationView bills={bills} taxInvoices={taxInvoices} payments={payments} summary={s} formatDate={formatDate} />
      )}

      {/* AGEING VIEW */}
      {activeView === 'ageing' && (
        <AgeingView ageing={ageing} bills={bills} formatDate={formatDate} />
      )}

      {/* Bill Modal */}
      {billModal && (
        <BillModal form={billForm} setForm={setBillForm} lineItems={billLineItems} setLineItems={setBillLineItems}
          editing={editingBill} billingContext={billingContext} onSave={handleSaveBill} onClose={() => setBillModal(false)} />
      )}

      {/* Payment Modal */}
      {payModal && (
        <PaymentModal form={payForm} setForm={setPayForm} bills={bills} onSave={handleCreatePayment} onClose={() => setPayModal(false)} />
      )}

      {/* Enforcement Override Modal */}
      {enforcementModal && (
        <EnforcementModal
          title={enforcementModal.title}
          blocks={enforcementModal.blocks}
          reason={overrideReason}
          setReason={setOverrideReason}
          userRole={user?.role}
          onOverride={async () => {
            if (!overrideReason.trim() || overrideReason.length < 5) { toast.error('Override reason is required (min 5 characters)'); return; }
            try {
              await enforcementModal.onOverride(overrideReason);
              setOverrideReason('');
            } catch (e) { toast.error(e.response?.data?.detail || 'Override failed'); }
          }}
          onCancel={() => { setEnforcementModal(null); setOverrideReason(''); }}
        />
      )}

      {/* Tax Invoice Modal */}
      {tiModal && (
        <TaxInvoiceModal form={tiForm} setForm={setTiForm} editing={editingTi} bills={bills} onSave={handleSaveTi} onClose={() => setTiModal(false)} />
      )}
    </div>
  );
};


/* ========== VO BILLING WARNING ========== */
const VOBillingWarning = ({ context }) => (
  <div className="border-2 border-orange-300 bg-orange-50 p-4" data-testid="vo-billing-warning">
    <div className="flex items-start gap-3">
      <Warning size={20} weight="fill" className="text-orange-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h3 className="text-sm font-bold text-orange-800">Approved VOs Not Fully Billed</h3>
        <p className="text-xs text-orange-700 mt-1">
          <strong>{context.unbilled_vos.length} VO(s)</strong> worth <strong>{fmt(context.unbilled_vo_value)}</strong> have not been included in billing.
        </p>
        <div className="mt-2 space-y-1">
          {context.unbilled_vos.map(v => (
            <div key={v.vo_id} className="text-xs text-orange-700 flex items-center gap-2">
              <Warning size={10} weight="fill" />
              <span className="font-mono font-bold">{v.vo_number}</span>
              <span>{v.title}</span>
              <span className="ml-auto font-bold">{fmt(v.unbilled_amount)} unbilled</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ========== VO BILLING TRACKER ========== */
const VOBillingTracker = ({ tracker }) => (
  <div className="bg-white border border-zinc-200 p-4" data-testid="vo-billing-tracker">
    <h3 className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-3">VO Billing Tracker</h3>
    <div className="space-y-2">
      {tracker.map(v => {
        const pct = v.total_amount > 0 ? Math.min(100, (v.billed_amount / v.total_amount) * 100) : 0;
        const statusColor = v.billing_status === 'Fully Billed' ? 'text-green-700' : v.billing_status === 'Partially Billed' ? 'text-amber-700' : 'text-red-700';
        return (
          <div key={v.vo_id} className="flex items-center gap-3">
            <span className="text-xs font-mono font-bold text-zinc-500 w-16">{v.vo_number}</span>
            <span className="text-xs text-zinc-700 flex-1 truncate">{v.title}</span>
            <div className="w-32 h-2 bg-zinc-100 overflow-hidden"><div className={`h-full ${v.billing_status === 'Fully Billed' ? 'bg-green-500' : v.billing_status === 'Partially Billed' ? 'bg-amber-500' : 'bg-red-300'}`} style={{ width: `${pct}%` }}></div></div>
            <span className={`text-xs font-bold ${statusColor} w-28 text-right`}>{v.billing_status}</span>
            <span className="text-xs text-zinc-500 w-24 text-right">{fmt(v.billed_amount)} / {fmt(v.total_amount)}</span>
          </div>
        );
      })}
    </div>
  </div>
);

/* ========== BILLS VIEW ========== */
const BillsView = ({ bills, statusDropdown, setStatusDropdown, handleStatusChange, handleDeleteBill, openBillModal, taxInvoices, formatDate }) => (
  <div className="bg-white border border-zinc-200">
    <div className="px-4 py-2.5 border-b border-zinc-100">
      <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Itemised Bills</span>
    </div>
    {bills.length === 0 ? (
      <div className="py-12 text-center">
        <Receipt size={36} className="mx-auto text-zinc-300 mb-2" />
        <p className="text-sm font-medium text-zinc-600">No bills created yet</p>
        <p className="text-xs text-zinc-400 mt-1">Click "Create Bill" to start billing for this project</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="bills-table">
          <thead>
            <tr className="text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
              <th className="px-3 py-2 text-left">Bill #</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Due Date</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">Received</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Invoice Status</th>
              <th className="px-3 py-2 text-center">VOs</th>
              <th className="px-3 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {bills.map(bill => (
              <tr key={bill.id} className="hover:bg-zinc-50">
                <td className="px-3 py-2 font-mono text-xs text-zinc-500">{bill.bill_number}</td>
                <td className="px-3 py-2 text-xs">{bill.bill_type}</td>
                <td className="px-3 py-2 text-xs text-zinc-700 max-w-[160px] truncate">{bill.description || '—'}</td>
                <td className="px-3 py-2 text-xs text-zinc-500">{formatDate(bill.bill_date)}</td>
                <td className="px-3 py-2 text-xs text-zinc-500" data-testid={`bill-due-${bill.bill_number}`}>{bill.due_date ? formatDate(bill.due_date) : '—'}</td>
                <td className="px-3 py-2 text-xs font-medium text-right">{fmt(bill.bill_amount)}</td>
                <td className="px-3 py-2 text-xs text-right text-green-700">{fmt(bill.amount_received)}</td>
                <td className="px-3 py-2 relative">
                  <button onClick={() => setStatusDropdown(statusDropdown === bill.id ? null : bill.id)} className="flex items-center gap-1" data-testid={`bill-status-${bill.bill_number}`}>
                    <Badge className={BILL_STATUS_COLORS[bill.status]}>{bill.status}</Badge>
                    <CaretDown size={10} className="text-zinc-400" />
                  </button>
                  {statusDropdown === bill.id && (
                    <div className="absolute z-20 top-full left-0 bg-white border border-zinc-200 shadow-lg py-1 min-w-[140px]">
                      {BILL_STATUSES.filter(st => st !== bill.status).map(st => (
                        <button key={st} onClick={() => handleStatusChange(bill.id, st)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50">{st}</button>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge className={INVOICE_STATUS_COLORS[bill.invoice_status] || INVOICE_STATUS_COLORS['Not Invoiced']}>
                    {bill.invoice_status || 'Not Invoiced'}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-center text-xs text-zinc-500">
                  {bill.linked_vo_ids?.length > 0 ? <span className="text-blue-700 font-bold">{bill.linked_vo_ids.length}</span> : '—'}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <button onClick={() => openBillModal(bill)} className="p-1 hover:bg-zinc-100 text-zinc-500" data-testid={`edit-bill-${bill.bill_number}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => handleDeleteBill(bill.id)} className="p-1 hover:bg-red-50 text-red-500" data-testid={`delete-bill-${bill.bill_number}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/* ========== TAX INVOICES VIEW ========== */
const TaxInvoicesView = ({ taxInvoices, bills, handleDeleteTi, openTiModal, formatDate, user }) => {
  const getBillNumbers = (billIds) => {
    if (!billIds?.length) return '—';
    return billIds.map(bid => {
      const b = bills.find(bb => bb.id === bid);
      return b ? b.bill_number : bid.slice(-6);
    }).join(', ');
  };

  return (
    <div className="bg-white border border-zinc-200">
      <div className="px-4 py-2.5 border-b border-zinc-100 flex justify-between items-center">
        <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Tax Invoice Register</span>
        <span className="text-[10px] text-zinc-400">Manual entries from Tally — no approval required</span>
      </div>
      {taxInvoices.length === 0 ? (
        <div className="py-12 text-center">
          <FileText size={36} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-sm font-medium text-zinc-600">No tax invoices registered yet</p>
          <p className="text-xs text-zinc-400 mt-1">Register tax invoices from Tally to track billing reconciliation.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tax-invoices-table">
            <thead>
              <tr className="text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
                <th className="px-3 py-2 text-left">Invoice #</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2 text-left">Linked Bills</th>
                <th className="px-3 py-2 text-left">Tax</th>
                <th className="px-3 py-2 text-left">E-Way</th>
                <th className="px-3 py-2 text-left">Remarks</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {taxInvoices.map(ti => (
                <tr key={ti.id} className="hover:bg-zinc-50">
                  <td className="px-3 py-2 font-mono text-xs font-bold text-blue-700">{ti.invoice_number}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{formatDate(ti.invoice_date)}</td>
                  <td className="px-3 py-2 text-xs font-medium text-right">{fmt(ti.invoice_value)}</td>
                  <td className="px-3 py-2 text-xs text-zinc-600">
                    <div className="flex items-center gap-1">
                      <LinkSimple size={12} className="text-zinc-400" />
                      {getBillNumbers(ti.linked_bill_ids)}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{ti.tax_type === 'igst' ? 'IGST' : 'CGST+SGST'} @ {ti.tax_rate}%</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">
                    {ti.eway_required ? (
                      <span className="text-green-700">{ti.eway_number || 'Required'}</span>
                    ) : <span className="text-zinc-400">No</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500 max-w-[120px] truncate">{ti.remarks || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex gap-1 justify-center">
                      <button onClick={() => openTiModal(ti)} className="p-1 hover:bg-zinc-100 text-zinc-500"><PencilSimple size={14} /></button>
                      {['admin', 'super_admin'].includes(user?.role) && (
                        <button onClick={() => handleDeleteTi(ti.id)} className="p-1 hover:bg-red-50 text-red-500"><Trash size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ========== PAYMENTS VIEW ========== */
const PaymentsView = ({ payments, handleDeletePayment, formatDate }) => (
  <div className="bg-white border border-zinc-200">
    <div className="px-4 py-2.5 border-b border-zinc-100">
      <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">All Payments</span>
    </div>
    {payments.length === 0 ? (
      <div className="py-12 text-center">
        <CreditCard size={36} className="mx-auto text-zinc-300 mb-2" />
        <p className="text-sm font-medium text-zinc-600">No payments recorded yet</p>
      </div>
    ) : (
      <table className="w-full text-sm" data-testid="payments-table">
        <thead>
          <tr className="text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
            <th className="px-3 py-2 text-left">Payment #</th>
            <th className="px-3 py-2 text-left">Bill Ref</th>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-left">Mode</th>
            <th className="px-3 py-2 text-left">Reference</th>
            <th className="px-3 py-2 text-left">Recorded By</th>
            <th className="px-3 py-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {payments.map(pay => (
            <tr key={pay.id} className="hover:bg-zinc-50">
              <td className="px-3 py-2 font-mono text-xs text-zinc-500">{pay.payment_id}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{pay.bill_number || '—'}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{formatDate(pay.payment_date)}</td>
              <td className="px-3 py-2 text-xs font-medium text-right text-green-700">{fmt(pay.amount)}</td>
              <td className="px-3 py-2 text-xs">{pay.mode}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{pay.reference_number || '—'}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{pay.recorded_by_name}</td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => handleDeletePayment(pay.id)} className="p-1 hover:bg-red-50 text-red-500"><Trash size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

/* ========== RECONCILIATION VIEW ========== */
const ReconciliationView = ({ bills, taxInvoices, payments, summary, formatDate }) => {
  const s = summary || {};
  return (
    <div className="space-y-4" data-testid="reconciliation-view">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-violet-200 p-4 text-center">
          <div className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Total Billed (Itemised)</div>
          <div className="text-2xl font-bold text-violet-700 mt-1">{fmt(s.total_billed)}</div>
          <div className="text-[10px] text-zinc-400">{s.bill_count} bill(s)</div>
        </div>
        <div className="bg-white border border-indigo-200 p-4 text-center">
          <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Total Invoiced (Tax Inv)</div>
          <div className="text-2xl font-bold text-indigo-700 mt-1">{fmt(s.total_invoiced)}</div>
          <div className="text-[10px] text-zinc-400">{s.invoice_count} invoice(s)</div>
        </div>
        <div className="bg-white border border-green-200 p-4 text-center">
          <div className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Total Received</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{fmt(s.total_received)}</div>
          <div className="text-[10px] text-zinc-400">{s.payment_count} payment(s)</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className={`bg-white border p-4 ${s.billed_vs_invoiced_gap > 0 ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <div className="flex items-center gap-2 mb-1">
            <ArrowsLeftRight size={14} className={s.billed_vs_invoiced_gap > 0 ? 'text-amber-600' : 'text-green-600'} />
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-600">Billed vs Invoiced Gap</span>
          </div>
          <div className={`text-xl font-bold ${s.billed_vs_invoiced_gap > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {s.billed_vs_invoiced_gap > 0 ? fmt(s.billed_vs_invoiced_gap) : 'Aligned'}
          </div>
          {s.billed_vs_invoiced_gap > 0 && <p className="text-[10px] text-amber-600 mt-1">Bills not yet reflected in Tax Invoices</p>}
        </div>
        <div className={`bg-white border p-4 ${s.invoiced_vs_received_gap > 0 ? 'border-orange-300 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
          <div className="flex items-center gap-2 mb-1">
            <ArrowsLeftRight size={14} className={s.invoiced_vs_received_gap > 0 ? 'text-orange-600' : 'text-green-600'} />
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-600">Invoiced vs Received Gap</span>
          </div>
          <div className={`text-xl font-bold ${s.invoiced_vs_received_gap > 0 ? 'text-orange-700' : 'text-green-700'}`}>
            {s.invoiced_vs_received_gap > 0 ? fmt(s.invoiced_vs_received_gap) : 'Fully Collected'}
          </div>
          {s.invoiced_vs_received_gap > 0 && <p className="text-[10px] text-orange-600 mt-1">Invoiced but not yet received from client</p>}
        </div>
      </div>

      {/* Bill-wise reconciliation */}
      <div className="bg-white border border-zinc-200">
        <div className="px-4 py-2.5 border-b border-zinc-100">
          <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Bill-wise Reconciliation</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
              <th className="px-3 py-2 text-left">Bill #</th>
              <th className="px-3 py-2 text-right">Bill Amount</th>
              <th className="px-3 py-2 text-right">Invoiced</th>
              <th className="px-3 py-2 text-right">Received</th>
              <th className="px-3 py-2 text-left">Bill Status</th>
              <th className="px-3 py-2 text-left">Invoice Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {bills.map(bill => (
              <tr key={bill.id} className="hover:bg-zinc-50">
                <td className="px-3 py-2 font-mono text-xs text-zinc-500">{bill.bill_number}</td>
                <td className="px-3 py-2 text-xs text-right font-medium">{fmt(bill.bill_amount)}</td>
                <td className="px-3 py-2 text-xs text-right text-indigo-700">{fmt(bill.amount_invoiced)}</td>
                <td className="px-3 py-2 text-xs text-right text-green-700">{fmt(bill.amount_received)}</td>
                <td className="px-3 py-2"><Badge className={BILL_STATUS_COLORS[bill.status]}>{bill.status}</Badge></td>
                <td className="px-3 py-2"><Badge className={INVOICE_STATUS_COLORS[bill.invoice_status] || ''}>{bill.invoice_status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ========== AGEING VIEW ========== */
const AgeingView = ({ ageing, bills, formatDate }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-4 gap-3">
      {[
        { label: '0 - 30 days', value: ageing['0_30'] || 0, color: 'border-l-green-500', textColor: 'text-green-700', bg: 'bg-green-50' },
        { label: '30 - 60 days', value: ageing['30_60'] || 0, color: 'border-l-amber-500', textColor: 'text-amber-700', bg: 'bg-amber-50' },
        { label: '60 - 90 days', value: ageing['60_90'] || 0, color: 'border-l-orange-500', textColor: 'text-orange-700', bg: 'bg-orange-50' },
        { label: '90+ days', value: ageing['90_plus'] || 0, color: 'border-l-red-500', textColor: 'text-red-700', bg: 'bg-red-50' },
      ].map((bucket, i) => (
        <div key={i} className={`${bucket.bg} border border-zinc-200 border-l-4 ${bucket.color} p-4`}>
          <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">{bucket.label}</div>
          <div className={`text-2xl font-bold mt-1 ${bucket.textColor}`}>{fmt(bucket.value)}</div>
        </div>
      ))}
    </div>
    <div className="bg-white border border-zinc-200 p-4">
      <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-3">Outstanding Bills Detail</div>
      {bills.filter(b => b.outstanding > 0 && b.status !== 'Draft').length === 0 ? (
        <div className="text-sm text-zinc-400 text-center py-4">No outstanding bills</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {bills.filter(b => b.outstanding > 0 && b.status !== 'Draft').map(bill => {
            const daysOld = Math.floor((new Date() - new Date(bill.bill_date)) / (1000 * 60 * 60 * 24));
            const bucketColor = daysOld > 90 ? 'text-red-600' : daysOld > 60 ? 'text-orange-600' : daysOld > 30 ? 'text-amber-600' : 'text-green-600';
            return (
              <div key={bill.id} className="flex items-center justify-between py-2">
                <div><div className="flex items-center gap-2"><span className="text-xs font-mono text-zinc-400">{bill.bill_number}</span><Badge className={BILL_STATUS_COLORS[bill.status]}>{bill.status}</Badge></div><div className="text-xs text-zinc-500 mt-0.5">{bill.description || '—'}</div></div>
                <div className="text-right"><div className="text-sm font-bold text-red-600">{fmt(bill.outstanding)}</div><div className={`text-[10px] font-semibold ${bucketColor}`}>{daysOld} days old</div></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);


/* ========== BILL MODAL ========== */
const BillModal = ({ form, setForm, lineItems, setLineItems, editing, billingContext, onSave, onClose }) => {
  const toggleVoLink = (voId) => {
    const ids = form.linked_vo_ids || [];
    setForm({ ...form, linked_vo_ids: ids.includes(voId) ? ids.filter(v => v !== voId) : [...ids, voId] });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="bill-modal">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <h2 className="text-sm font-bold text-zinc-900">{editing ? 'Edit Bill' : 'New Itemised Bill'}</h2>
          <button onClick={onClose}><X size={18} className="text-zinc-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* VO Enforcement Context */}
          {billingContext && !editing && (
            <div className="border border-blue-200 bg-blue-50 p-3 text-xs">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div><div className="text-[10px] text-blue-600 font-bold uppercase">BOQ Value</div><div className="font-bold text-zinc-800">{fmt(billingContext.boq_value)}</div></div>
                <div><div className="text-[10px] text-blue-600 font-bold uppercase">Approved VOs</div><div className="font-bold text-zinc-800">{fmt(billingContext.approved_vo_value)}</div></div>
                <div><div className="text-[10px] text-blue-600 font-bold uppercase">Already Billed</div><div className="font-bold text-zinc-800">{fmt(billingContext.total_billed)}</div></div>
                <div><div className="text-[10px] text-blue-600 font-bold uppercase">Balance to Bill</div><div className="font-bold text-blue-700">{fmt(billingContext.balance_to_bill)}</div></div>
              </div>
              {billingContext.has_unbilled_vos && (
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <div className="flex items-center gap-1 text-orange-700 font-bold mb-1">
                    <Warning size={12} weight="fill" /> Approved VOs not yet billed:
                  </div>
                  {billingContext.unbilled_vos.map(v => (
                    <label key={v.vo_id} className="flex items-center gap-2 py-0.5 cursor-pointer" data-testid={`link-vo-${v.vo_id}`}>
                      <input type="checkbox" checked={form.linked_vo_ids?.includes(v.vo_id)} onChange={() => toggleVoLink(v.vo_id)} className="rounded" />
                      <span className="font-mono font-bold">{v.vo_number}</span>
                      <span className="text-zinc-700">{v.title}</span>
                      <span className="ml-auto font-bold">{fmt(v.unbilled_amount)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {!editing && (
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Bill Type</label>
                <select value={form.bill_type} onChange={e => setForm({...form, bill_type: e.target.value})}
                  className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="bill-type-select">
                  {BILL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Bill Date</label>
              <Input type="date" value={form.bill_date} onChange={e => setForm({...form, bill_date: e.target.value})}
                className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="bill-date-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Due Date</label>
              <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}
                className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="bill-due-date-input" />
              <p className="text-[10px] text-zinc-400 mt-0.5">Used for ageing. Defaults to Bill Date + 30 days if left blank.</p>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Description</label>
            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="bill-desc-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Total Amount *</label>
              <Input type="number" value={form.bill_amount} onChange={e => setForm({...form, bill_amount: e.target.value})}
                className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="bill-amount-input" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Notes</label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="bill-notes-input" />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Line Items</label>
              <button onClick={() => setLineItems([...lineItems, { description: '', amount: 0, hsn_sac: '', quantity: 0, unit: '', rate: 0 }])}
                className="text-xs text-blue-700 hover:underline flex items-center gap-0.5" data-testid="add-bill-line-item">
                <Plus size={10} /> Add Item
              </button>
            </div>
            {lineItems.length > 0 && (
              <div className="border border-zinc-200 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="px-2 py-1.5 text-left font-bold text-zinc-500">Description</th>
                      <th className="px-2 py-1.5 text-left font-bold text-zinc-500 w-20">HSN/SAC</th>
                      <th className="px-2 py-1.5 text-right font-bold text-zinc-500 w-16">Qty</th>
                      <th className="px-2 py-1.5 text-left font-bold text-zinc-500 w-16">Unit</th>
                      <th className="px-2 py-1.5 text-right font-bold text-zinc-500 w-20">Rate</th>
                      <th className="px-2 py-1.5 text-right font-bold text-zinc-500 w-24">Amount</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((li, idx) => (
                      <tr key={idx} className="border-b border-zinc-100">
                        <td className="px-1 py-1">
                          <input value={li.description} onChange={e => { const u = [...lineItems]; u[idx] = {...u[idx], description: e.target.value}; setLineItems(u); }}
                            className="w-full border border-zinc-200 px-1.5 py-1 text-xs" placeholder="Description" />
                        </td>
                        <td className="px-1 py-1">
                          <input value={li.hsn_sac || ''} onChange={e => { const u = [...lineItems]; u[idx] = {...u[idx], hsn_sac: e.target.value}; setLineItems(u); }}
                            className="w-full border border-zinc-200 px-1.5 py-1 text-xs" placeholder="HSN" />
                        </td>
                        <td className="px-1 py-1">
                          <input type="number" value={li.quantity || ''} onChange={e => { const u = [...lineItems]; const qty = parseFloat(e.target.value) || 0; u[idx] = {...u[idx], quantity: qty, amount: qty * (u[idx].rate || 0)}; setLineItems(u); }}
                            className="w-full border border-zinc-200 px-1.5 py-1 text-xs text-right" />
                        </td>
                        <td className="px-1 py-1">
                          <input value={li.unit || ''} onChange={e => { const u = [...lineItems]; u[idx] = {...u[idx], unit: e.target.value}; setLineItems(u); }}
                            className="w-full border border-zinc-200 px-1.5 py-1 text-xs" placeholder="Sqft" />
                        </td>
                        <td className="px-1 py-1">
                          <input type="number" value={li.rate || ''} onChange={e => { const u = [...lineItems]; const rate = parseFloat(e.target.value) || 0; u[idx] = {...u[idx], rate, amount: (u[idx].quantity || 0) * rate}; setLineItems(u); }}
                            className="w-full border border-zinc-200 px-1.5 py-1 text-xs text-right" />
                        </td>
                        <td className="px-1 py-1 text-right font-medium text-zinc-700">{fmt(li.amount || 0)}</td>
                        <td className="px-1 py-1 text-center">
                          <button onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600"><Trash size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="rounded-none border-zinc-300 text-xs">Cancel</Button>
          <Button onClick={onSave} className="bg-zinc-900 text-white rounded-none text-xs" data-testid="submit-bill-btn">
            {editing ? 'Update Bill' : 'Create Bill'}
          </Button>
        </div>
      </div>
    </div>
  );
};


/* ========== PAYMENT MODAL ========== */
const PaymentModal = ({ form, setForm, bills, onSave, onClose }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="payment-modal">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200">
        <h2 className="text-sm font-bold text-zinc-900">Record Payment</h2>
        <button onClick={onClose}><X size={18} className="text-zinc-400" /></button>
      </div>
      <div className="p-5 space-y-3">
        <div>
          <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Against Bill (optional)</label>
          <select value={form.bill_id} onChange={e => setForm({...form, bill_id: e.target.value})}
            className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="pay-bill-select">
            <option value="">No specific bill</option>
            {bills.filter(b => b.status !== 'Paid').map(b => (
              <option key={b.id} value={b.id}>{b.bill_number} — Rs.{(b.outstanding || 0).toLocaleString('en-IN')} outstanding</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Amount *</label>
            <Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
              className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="pay-amount-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Date</label>
            <Input type="date" value={form.payment_date} onChange={e => setForm({...form, payment_date: e.target.value})}
              className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="pay-date-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Mode *</label>
            <select value={form.mode} onChange={e => setForm({...form, mode: e.target.value})}
              className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="pay-mode-select">
              {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Reference #</label>
            <Input value={form.reference_number} onChange={e => setForm({...form, reference_number: e.target.value})}
              className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="pay-ref-input" />
          </div>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Remarks</label>
          <Input value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})}
            className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="pay-remarks-input" />
        </div>
      </div>
      <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
        <Button variant="outline" onClick={onClose} className="rounded-none border-zinc-300 text-xs">Cancel</Button>
        <Button onClick={onSave} className="bg-zinc-900 text-white rounded-none text-xs" data-testid="submit-payment-btn">Record Payment</Button>
      </div>
    </div>
  </div>
);


/* ========== E-WAY COMPLIANCE CHECK ========== */
const EwayComplianceCheck = ({ form, docType = 'invoice' }) => {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const runCheck = async () => {
    setChecking(true);
    try {
      const hsnList = (form.hsn_codes || '').split(',').map(s => s.trim()).filter(Boolean);
      const { data } = await axios.post(`${BACKEND_URL}/api/eway/validate`, {
        doc_type: docType,
        consignment_value: parseFloat(form.invoice_value) || 0,
        is_interstate: !!form.is_interstate,
        from_state_code: form.from_state_code || '',
        hsn_codes: hsnList,
        sac_codes: hsnList.filter(h => h.startsWith('9988')),
        is_job_work: !!form.is_job_work,
        doc_date: form.invoice_date || undefined,
        eway_bill_number: form.eway_number || '',
        eway_bill_date: form.eway_date || undefined,
        eway_bill_validity_date: form.eway_bill_validity_date || undefined,
        ship_to_gstin: form.ship_to_gstin || '',
      }, { withCredentials: true });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Compliance check failed'); }
    finally { setChecking(false); }
  };

  return (
    <div>
      <Button type="button" onClick={runCheck} disabled={checking} variant="outline"
        className="rounded-none border-blue-300 text-blue-700 h-7 text-xs hover:bg-blue-50" data-testid="eway-check-btn">
        {checking ? 'Checking…' : 'Check E-Way Compliance'}
      </Button>
      {result && (
        <div className={`mt-2 border p-2.5 text-xs ${result.compliant ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="eway-result">
          <p className="font-bold">
            {result.required ? 'E-Way Bill REQUIRED' : 'E-Way Bill not required'} ·{' '}
            <span className={result.compliant ? 'text-green-700' : 'text-red-700'}>
              {result.compliant ? 'Compliant ✓' : 'Non-compliant ✗'}
            </span>
          </p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Threshold: ₹{(result.threshold || 0).toLocaleString('en-IN')} ({result.basis}){result.is_job_work ? ' · Job work' : ''}</p>
          {(result.errors || []).map((e, i) => <p key={i} className="text-red-700 mt-1">• {e}</p>)}
          {(result.warnings || []).map((w, i) => <p key={i} className="text-amber-700 mt-1">⚠ {w}</p>)}
        </div>
      )}
    </div>
  );
};


/* ========== TAX INVOICE MODAL ========== */
const TaxInvoiceModal = ({ form, setForm, editing, bills, onSave, onClose }) => {
  const toggleBillLink = (billId) => {
    const ids = form.linked_bill_ids || [];
    setForm({ ...form, linked_bill_ids: ids.includes(billId) ? ids.filter(b => b !== billId) : [...ids, billId] });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="tax-invoice-modal">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <h2 className="text-sm font-bold text-zinc-900">{editing ? 'Edit Tax Invoice' : 'Register Tax Invoice'}</h2>
          <button onClick={onClose}><X size={18} className="text-zinc-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-zinc-500">Register a tax invoice from Tally. Link it to one or more itemised bills for reconciliation.</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Invoice Number *</label>
              <Input value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})}
                className="mt-1 rounded-none border-zinc-300 h-9 text-sm" placeholder="e.g. SGD/INV/2026/001" data-testid="ti-number-input" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Invoice Date</label>
              <Input type="date" value={form.invoice_date} onChange={e => setForm({...form, invoice_date: e.target.value})}
                className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="ti-date-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Invoice Value *</label>
              <Input type="number" value={form.invoice_value} onChange={e => setForm({...form, invoice_value: e.target.value})}
                className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="ti-value-input" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Tax Type</label>
              <select value={form.tax_type} onChange={e => setForm({...form, tax_type: e.target.value})}
                className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="ti-tax-type">
                <option value="cgst_sgst">CGST + SGST</option>
                <option value="igst">IGST</option>
              </select>
            </div>
          </div>

          {/* E-Way Bill (June 2026 compliance) */}
          <div className="border border-zinc-200 bg-zinc-50 p-3" data-testid="ti-eway-section">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.eway_required} onChange={e => setForm({...form, eway_required: e.target.checked})} className="rounded" data-testid="ti-eway-check" />
              <span className="text-xs font-semibold text-zinc-700">E-Way Bill Details</span>
            </label>
            {form.eway_required && (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-semibold text-zinc-500">E-Way Bill Number</label>
                    <Input value={form.eway_number} onChange={e => setForm({...form, eway_number: e.target.value})}
                      className="mt-1 rounded-none border-zinc-300 h-8 text-xs" data-testid="ti-eway-number" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-zinc-500">E-Way Bill Date</label>
                    <Input type="date" value={form.eway_date} onChange={e => setForm({...form, eway_date: e.target.value})}
                      className="mt-1 rounded-none border-zinc-300 h-8 text-xs" data-testid="ti-eway-date" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-zinc-500">Validity Date</label>
                    <Input type="date" value={form.eway_bill_validity_date || ''} onChange={e => setForm({...form, eway_bill_validity_date: e.target.value})}
                      className="mt-1 rounded-none border-zinc-300 h-8 text-xs" data-testid="ti-eway-validity" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-zinc-500">Ship-to GSTIN *</label>
                    <Input value={form.ship_to_gstin || ''} onChange={e => setForm({...form, ship_to_gstin: e.target.value})}
                      className="mt-1 rounded-none border-zinc-300 h-8 text-xs" placeholder="e.g. 27AAAAA0000A1Z5" data-testid="ti-ship-to-gstin" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-zinc-500">From State Code</label>
                    <Input value={form.from_state_code || ''} onChange={e => setForm({...form, from_state_code: e.target.value.toUpperCase()})}
                      className="mt-1 rounded-none border-zinc-300 h-8 text-xs" placeholder="e.g. MH" data-testid="ti-from-state" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-zinc-500">HSN/SAC Codes (comma-separated)</label>
                    <Input value={form.hsn_codes || ''} onChange={e => setForm({...form, hsn_codes: e.target.value})}
                      className="mt-1 rounded-none border-zinc-300 h-8 text-xs" placeholder="e.g. 940360, 998821" data-testid="ti-hsn-codes" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-zinc-600">
                    <input type="checkbox" checked={!!form.is_interstate} onChange={e => setForm({...form, is_interstate: e.target.checked})} data-testid="ti-interstate" /> Interstate
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-zinc-600">
                    <input type="checkbox" checked={!!form.is_job_work} onChange={e => setForm({...form, is_job_work: e.target.checked})} data-testid="ti-jobwork" /> Job Work (SAC 998821)
                  </label>
                </div>
                <EwayComplianceCheck form={form} docType="invoice" />
              </div>
            )}
          </div>

          {/* Link Bills (many-to-many) */}
          <div>
            <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Link to Itemised Bills</label>
            <p className="text-[10px] text-zinc-400 mt-0.5 mb-1">One invoice can link to multiple bills, and vice versa.</p>
            {bills.length === 0 ? (
              <p className="text-xs text-zinc-400 italic">No bills available to link</p>
            ) : (
              <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-40 overflow-y-auto">
                {bills.map(b => (
                  <label key={b.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-zinc-50" data-testid={`link-bill-${b.bill_number}`}>
                    <input type="checkbox" checked={form.linked_bill_ids?.includes(b.id)} onChange={() => toggleBillLink(b.id)} className="rounded" />
                    <span className="text-xs font-mono text-zinc-500">{b.bill_number}</span>
                    <span className="text-xs text-zinc-700">{b.bill_type}</span>
                    <span className="text-xs font-bold ml-auto">{fmt(b.bill_amount)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Remarks</label>
            <textarea value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})}
              className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm resize-none h-14" data-testid="ti-remarks-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="rounded-none border-zinc-300 text-xs">Cancel</Button>
          <Button onClick={onSave} className="bg-blue-700 text-white rounded-none text-xs" data-testid="submit-ti-btn">
            {editing ? 'Update Tax Invoice' : 'Register Tax Invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
};


/* ========== ENFORCEMENT OVERRIDE MODAL ========== */
const EnforcementModal = ({ title, blocks, reason, setReason, userRole, onOverride, onCancel }) => {
  const canOverride = blocks.every(b => {
    const reqRole = b.override_role || 'super_admin';
    if (reqRole === 'super_admin') return userRole === 'super_admin';
    if (reqRole === 'admin') return ['admin', 'super_admin'].includes(userRole);
    return false;
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="enforcement-modal">
      <div className="bg-white w-full max-w-lg shadow-2xl border-2 border-red-300">
        <div className="bg-red-50 border-b-2 border-red-300 px-5 py-3.5 flex items-center gap-3">
          <Warning size={22} weight="fill" className="text-red-600" />
          <h2 className="text-sm font-bold text-red-900">{title || 'Enforcement Block'}</h2>
        </div>
        <div className="p-5 space-y-3">
          {blocks.map((block, i) => (
            <div key={i} className="border border-red-200 bg-red-50 p-3 text-sm">
              <div className="flex items-start gap-2">
                <Warning size={14} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-800 font-semibold">{block.message}</p>
                  {block.total_impact > 0 && (
                    <p className="text-xs text-red-600 mt-0.5 font-bold">Financial impact: {fmt(block.total_impact)}</p>
                  )}
                  {Array.isArray(block.data) && block.data.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {block.data.map((d, j) => (
                        <li key={j} className="text-xs text-red-700 flex items-center gap-1">
                          <span className="font-mono font-bold">{d.vo_number || d.bill_number || ''}</span>
                          <span>{d.title || d.description || ''}</span>
                          {d.total_amount > 0 && <span className="ml-auto font-bold">{fmt(d.total_amount)}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[10px] text-red-500 mt-1">Override requires: <strong>{block.override_role}</strong></p>
                </div>
              </div>
            </div>
          ))}

          {canOverride ? (
            <div className="border-2 border-amber-300 bg-amber-50 p-3">
              <label className="text-xs font-bold text-amber-800 block mb-1">Override Reason (mandatory)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border border-amber-300 bg-white px-3 py-2 text-sm resize-none h-16"
                placeholder="Provide justification for bypassing this enforcement rule..."
                data-testid="enforcement-override-reason"
              />
            </div>
          ) : (
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-center">
              <p className="text-sm text-zinc-600">You do not have permission to override this rule.</p>
              <p className="text-xs text-zinc-500 mt-0.5">Contact your Super Admin to proceed.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
          <Button variant="outline" onClick={onCancel} className="rounded-none border-zinc-300 text-xs" data-testid="enforcement-cancel-btn">Go Back</Button>
          {canOverride && (
            <Button onClick={onOverride} className="bg-red-700 text-white rounded-none text-xs" data-testid="enforcement-override-btn">
              Override & Proceed
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
